import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode } from '@aws-amplify/auth';
import { Form, Button, Alert } from 'react-bootstrap';
import './LoginPage.css';

export function VerifyAccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const usernameFromUrl = searchParams.get('username') || searchParams.get('email') || '';

  const [destination, setDestination] = useState(searchParams.get('destination') || '');
  const [username, setUsername] = useState(usernameFromUrl);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  const [resendMessage, setResendMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await confirmSignUp({ username: username.trim().toLowerCase(), confirmationCode: code });

      sessionStorage.removeItem('pendingRegistration');

      navigate('/login', { state: { signupSuccess: true } });
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendMessage(null);
    setError(null);
    setResending(true);
    try {
      const result = await resendSignUpCode({ username: username.trim().toLowerCase() });
      if (result.destination) setDestination(result.destination);
      setResendMessage('A new verification code has been sent.');
    } catch (err) {
      setError(err.message || 'Failed to resend code.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="login-container">
      <div className="tg-auth-card">
        <div className="tg-auth-brand">
          <div className="tg-auth-brand-mark">T</div>
          <span className="tg-auth-brand-name">Tegrat Auto</span>
        </div>

        <h2 className="tg-auth-title">Verify your account</h2>
        <p className="tg-auth-subtitle">
          Enter the verification code sent to{' '}
          {destination ? <strong>{destination}</strong> : 'the email address used during signup'}.
        </p>

        <Form onSubmit={handleSubmit}>
          {!usernameFromUrl && <Form.Group className="mb-3" controlId="verifyUsername">
            <Form.Label>Signup email or phone number</Form.Label>
            <Form.Control value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" required disabled={loading} />
          </Form.Group>}
          <Form.Group className="mb-3" controlId="code">
            <Form.Label>Verification Code</Form.Label>
            <Form.Control
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="6-digit code"
              disabled={loading}
              required
              autoFocus={!!usernameFromUrl}
            />
          </Form.Group>

          {error && <Alert variant="danger">{error}</Alert>}
          {resendMessage && <Alert variant="success">{resendMessage}</Alert>}

          <div className="d-grid gap-2">
            <Button variant="primary" type="submit" disabled={loading} size="lg">
              {loading ? 'Verifying…' : 'Verify Account'}
            </Button>
          </div>
        </Form>

        <hr className="tg-auth-divider" />
        <div className="tg-auth-footer">
          <a
            href="#resend"
            onClick={(e) => { e.preventDefault(); handleResend(); }}
            style={resending ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
          >
            {resending ? 'Sending…' : "Didn't receive a code? Resend"}
          </a>
          <span className="tg-auth-dot">·</span>
          <Link to="/">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default VerifyAccountPage;
